import { useTranslation } from "react-i18next";
import {
  isLegQuotingClosed,
  legStatusBadgeVariant,
} from "@/utils/consumerQuoteRequestView";

import { StatusBadge } from "@/components/shared/StatusBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

import { UserAvatar } from "@/components/ui/user-avatar";
import { useAwardedProfessionalAvatarObjectUrl } from "@/hooks/useAwardedProfessionalAvatarObjectUrl";
import {
  QuoteChargeSummary,
} from "@/components/features/notarial-requests/quote-charge-summary";
import { formatUyMinor } from "@/lib/formatUyMinor";
import type {
  QuoteRequestCharge,
  QuoteRequestLegView,
} from "@/services/quoteRequestsService";

export interface QuoteRequestLegsCardProps {
  /** Needed to build the per-leg avatar route. */
  quoteRequestId: string;
  legs: QuoteRequestLegView[];
  /**
   * Server-computed subtotal, service fee and total (spec platform-fee-model
   * §3d). Null until something is awarded.
   *
   * This card used to add the awards up itself, which was right only while the
   * charge was exactly that sum — it cannot know the platform's fee, and the
   * consumer would have read one number and been charged another.
   */
  charge: QuoteRequestCharge | null;
  /** Pays the whole request, once. Disabled while a checkout is in flight. */
  onPay: () => void;
  isPaying: boolean;
}

/** Legs that are still deciding — no amount is owed for these yet. */
const OPEN_STATUSES = new Set(["triaged", "quoting"]);

/**
 * Per-discipline breakdown for a request that needs more than one professional
 * (spec 27c505ed, L6).
 *
 * Each discipline is quoted and awarded separately, so the consumer is owed
 * two things this card exists to give them: what each half costs, and one
 * place to see that a half is still open while the other has already moved on.
 *
 * Payment is NOT per discipline (spec rfq-single-payment, reversing 27c505ed
 * §3.1). The consumer's counterparty is Gestionalo, which then pays each
 * professional, so there is one pay action for the request — and it does not
 * appear while any discipline is still collecting bids, because the amount
 * would be missing a leg they are about to owe for.
 */
export function QuoteRequestLegsCard({
  quoteRequestId,
  legs,
  charge,
  onPay,
  isPaying,
}: QuoteRequestLegsCardProps): JSX.Element {
  const { t } = useTranslation();

  const settled = legs.filter((leg) => leg.award != null);
  // Something is owed when a leg is awarded and its payment has not cleared.
  const awaitsPayment = legs.some(
    (leg) => leg.status === "awarded" && leg.payment?.status !== "paid",
  );
  // Only a total once nothing is still being quoted — a "total" that is
  // missing a leg still out for bids is a number the consumer would plan
  // around and then find wrong.
  const anyStillOpen = legs.some((leg) => OPEN_STATUSES.has(leg.status));
  // Everything paid and under way: the card stops being a cost breakdown and
  // becomes the way into the two trámites. The money is settled, so it stops
  // leading — "Profesionales y costos" describes a decision already made.
  const allStarted =
    legs.length > 0 && legs.every((leg) => leg.childRequestId != null);
  // What the figure means right now: still collecting, owed, or already paid.
  const totalLabelKey = anyStillOpen
    ? "requests.user.quoteLegsAgreedSoFar"
    : allStarted
      ? "requests.user.quoteLegsTotalPaid"
      : "requests.user.quoteLegsTotal";

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">
          {t(allStarted ? "requests.user.quoteLegsTitleStarted" : "requests.user.quoteLegsTitle")}
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          {t(allStarted ? "requests.user.quoteLegsHintStarted" : "requests.user.quoteLegsHint")}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {legs.map((leg) => (
          <QuoteRequestLegRow
            key={leg.id}
            quoteRequestId={quoteRequestId}
            leg={leg}
          />
        ))}

        {/* Nothing awarded yet means there is no total to state — an
            "Acordado hasta ahora — —" row is a label with no content, and
            reads as data that failed to load. */}
        {settled.length > 0 && charge != null ? (
          <QuoteChargeSummary
            charge={charge}
            totalLabelKey={totalLabelKey}
            canPay={!anyStillOpen && awaitsPayment}
            isSettled={allStarted}
            blockedNoteKey={
              anyStillOpen ? "requests.user.quoteLegsWaitingOtherDiscipline" : null
            }
            onPay={onPay}
            isPaying={isPaying}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}

function QuoteRequestLegRow({
  quoteRequestId,
  leg,
}: {
  quoteRequestId: string;
  leg: QuoteRequestLegView;
}): JSX.Element {
  const { t } = useTranslation();
  const isPaid = leg.payment?.status === "paid";
  const avatarSrc = useAwardedProfessionalAvatarObjectUrl(
    quoteRequestId,
    leg.id,
    leg.award?.professionalProfileImage,
  );

  const childHref = leg.childRequestId ? `/requests/${leg.childRequestId}` : null;

  // Once the trámite exists the ENTIRE row is the way into it, not a text
  // link at the end of it. "Ver trámite →" was the primary action on this
  // card and read as a footnote; a row that navigates has to look like one,
  // so it takes a hover state, a focus ring and a chevron, and the link
  // itself goes away.
  const body = (
    <>
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-foreground">
            {t(`requests.user.quoteDiscipline_${leg.discipline}`)}
          </span>
          {/*
            PER LEG, and on the leg's OWN window. This badge said "Recibiendo
            presupuestos" in blue directly under a request badge saying
            quoting had closed — the same screen contradicting itself. A leg
            stays 'quoting' for the whole award grace after its window shuts,
            and the two legs of one RFQ can lapse days apart, so each row has
            to ask its own clock.
          */}
          <StatusBadge
            variant={isLegQuotingClosed(leg) ? "warning" : legStatusBadgeVariant(leg.status)}
          >
            {isLegQuotingClosed(leg)
              ? t("requests.user.quoteLegStatus_quoting_closed")
              : t(`requests.user.quoteLegStatus_${leg.status}`, {
                  defaultValue: leg.status,
                })}
          </StatusBadge>
        </div>
        {leg.award?.professionalDisplayName ? (
          // Who they hired, with a face and the credentials that let them
          // check it against the public register. A display name alone asks a
          // consumer to commit money to a string. Same vocabulary as
          // AssignedProfessionalCard on the other request types — avatar,
          // name, register numbers — at the scale a leg row allows.
          <div className="flex min-w-0 items-center gap-2.5">
            <UserAvatar
              displayName={leg.award.professionalDisplayName}
              imageSrc={avatarSrc}
              size="sm"
            />
            <div className="min-w-0 space-y-0.5">
              <p className="truncate text-sm text-foreground">
                {leg.award.professionalDisplayName}
              </p>
              {leg.award.professionalCedula ||
              leg.award.professionalMatricula ? (
                <p className="truncate text-xs text-muted-foreground">
                  {[
                    leg.award.professionalCedula
                      ? t("requests.user.quoteAwardCedula", {
                          value: leg.award.professionalCedula,
                        })
                      : null,
                    leg.award.professionalMatricula
                      ? t("requests.user.quoteAwardMatricula", {
                          value: leg.award.professionalMatricula,
                        })
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      <div className="flex items-center gap-3">
        {/* Demoted once paid: the amount is settled and the row is now about
            opening the work, so it stops competing with it. */}
        <span
          className={
            childHref != null
              ? "text-sm tabular-nums text-muted-foreground"
              : "text-sm tabular-nums text-foreground"
          }
        >
          {leg.award
            ? formatUyMinor(leg.award.priceMinor, leg.award.currency)
            : "—"}
        </span>
        {isPaid ? (
          <StatusBadge variant="success">
            {t("requests.user.quoteLegPaidBadge")}
          </StatusBadge>
        ) : null}
        {childHref != null ? (
          <ChevronRight
            className="size-4 shrink-0 text-muted-foreground transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-foreground"
            aria-hidden
          />
        ) : null}
      </div>
    </>
  );

  const shell =
    "flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card p-3";

  if (childHref == null) return <div className={shell}>{body}</div>;

  return (
    <Link
      to={childHref}
      // Where back should return to. Without it the child's back button goes
      // to the list, dropping the consumer out of the job they were reading.
      state={{ from: `/requests/${quoteRequestId}?source=quote` }}
      aria-label={t("requests.user.quoteLegOpenChildAria", {
        discipline: t(`requests.user.quoteDiscipline_${leg.discipline}`),
      })}
      className={`group ${shell} transition-colors duration-150 hover:border-primary/40 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2`}
    >
      {body}
    </Link>
  );
}


